/**
 * SRE Landing Page (Premium Dark)
 * ================================
 * Entry point for the SlabReality Engine.
 * 
 * Design: Premium Dark (#121212) - Technical, Precise, Elegant
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, ArrowRight, Layers, Grid3X3, Eye } from 'lucide-react';
import { useAuth } from '../../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SRELandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Track SRE page opened
  useEffect(() => {
    const trackSREOpen = async () => {
      try {
        await fetch(`${BACKEND_URL}/api/sre/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            event: 'sre_opened',
            user_id: user?.user_id,
            timestamp: new Date().toISOString()
          })
        });
      } catch (err) {
        console.log('Analytics tracking failed:', err);
      }
    };
    
    if (user) {
      trackSREOpen();
    }
  }, [user]);

  const handleStartProject = async () => {
    // Track project start
    try {
      await fetch(`${BACKEND_URL}/api/sre/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event: 'sre_project_started',
          user_id: user?.user_id,
          timestamp: new Date().toISOString()
        })
      });
    } catch (err) {
      console.log('Analytics tracking failed:', err);
    }
    
    navigate('/sre/project');
  };

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="sre-landing-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A] to-[#121212]" />
        
        {/* Content */}
        <div className="relative max-w-4xl mx-auto px-6 py-24 md:py-36">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full mb-10">
              <span className="text-xs font-medium text-[#A0A0A0] uppercase tracking-wider">Beta</span>
            </div>
            
            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-8 tracking-tight">
              SlabReality Engine
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-[#A0A0A0] mb-4 max-w-2xl mx-auto leading-relaxed font-light">
              Place real stone slabs into real spaces.
            </p>
            <p className="text-base text-[#666666] mb-14 max-w-xl mx-auto">
              What you see is exactly what you get.
              <br />
              No AI generation. 100% deterministic projection.
            </p>
            
            {/* CTA Button */}
            <button
              onClick={handleStartProject}
              className="group inline-flex items-center gap-3 px-8 py-4 bg-[#D6D6D6] hover:bg-white text-[#121212] text-base font-medium rounded transition-all duration-200"
              data-testid="sre-start-project-btn"
            >
              <Box className="w-5 h-5" />
              Start New Project
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-4xl mx-auto px-6 py-20 border-t border-[#2A2A2A]">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Feature 1 */}
          <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg hover:border-[#3A3A3A] transition-colors">
            <div className="w-10 h-10 bg-[#2A2A2A] rounded flex items-center justify-center mb-4">
              <Layers className="w-5 h-5 text-[#A0A0A0]" />
            </div>
            <h3 className="text-base font-medium text-white mb-2">Real Slabs</h3>
            <p className="text-[#666666] text-sm leading-relaxed">
              Upload actual slab photos. The system preserves every vein, every color, every detail.
            </p>
          </div>

          {/* Feature 2 */}
          <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg hover:border-[#3A3A3A] transition-colors">
            <div className="w-10 h-10 bg-[#2A2A2A] rounded flex items-center justify-center mb-4">
              <Grid3X3 className="w-5 h-5 text-[#A0A0A0]" />
            </div>
            <h3 className="text-base font-medium text-white mb-2">Precise Geometry</h3>
            <p className="text-[#666666] text-sm leading-relaxed">
              Perspective-correct projection using real-world dimensions. Calculate material needs instantly.
            </p>
          </div>

          {/* Feature 3 */}
          <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg hover:border-[#3A3A3A] transition-colors">
            <div className="w-10 h-10 bg-[#2A2A2A] rounded flex items-center justify-center mb-4">
              <Eye className="w-5 h-5 text-[#A0A0A0]" />
            </div>
            <h3 className="text-base font-medium text-white mb-2">WYSIWYG</h3>
            <p className="text-[#666666] text-sm leading-relaxed">
              What You See Is What You Get. No surprises. The final result matches the visualization exactly.
            </p>
          </div>
        </div>
      </div>

      {/* How it Works Section */}
      <div className="max-w-4xl mx-auto px-6 py-20 border-t border-[#2A2A2A]">
        <h2 className="text-lg font-light text-white text-center mb-12 tracking-wide">How It Works</h2>
        
        <div className="grid grid-cols-4 gap-4">
          {[
            { step: 1, title: 'Upload Space', desc: 'Environment photo' },
            { step: 2, title: 'Select Slab', desc: 'Stone texture' },
            { step: 3, title: 'Define Surface', desc: '4 corner points' },
            { step: 4, title: 'Generate', desc: 'Instant result' },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-8 h-8 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xs font-medium text-[#A0A0A0]">{item.step}</span>
              </div>
              <h4 className="text-sm font-medium text-white mb-1">{item.title}</h4>
              <p className="text-xs text-[#666666]">{item.desc}</p>
            </div>
          ))}
        </div>
        
        {/* Secondary CTA */}
        <div className="text-center mt-14">
          <button
            onClick={handleStartProject}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white text-sm font-medium rounded border border-[#2A2A2A] transition-colors"
            data-testid="sre-start-project-btn-secondary"
          >
            Begin Now
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-4xl mx-auto px-6 py-10 border-t border-[#2A2A2A] text-center">
        <p className="text-xs text-[#666666]">
          SlabReality Engine is currently in Beta. Your feedback helps us improve.
        </p>
      </div>
    </div>
  );
};

export default SRELandingPage;
